import styled, { css } from 'styled-components/macro';
import { CheckboxCheck, CheckboxUncheck } from '@yme/assets/icons';
import { LIGHT_TYPE } from '@yme/tokens/colorSchema';
import { colorBlackTransparent100, colorBlackTransparent200, colorCoreBlack300, colorCoreBlack900Base, colorCoreWhite1000, colorWhiteTransparent600, colorWhiteTransparent800, defaultOutlineColor, defaultTransparent, extraGrayColor, } from '@yme/tokens/colors';
import { RegularTextLevel5 } from '@yme/tokens/typography';
import { Z_INDEX } from '@yme/tokens/zindex';
import { getTextStyles } from '@yme/utils/styles';
import { CUSTOM_LABEL_INDENT } from './constants';
export const CheckboxStyledWrapper = styled.div `
  ${getTextStyles('Ubuntu', 400, 16, 24)}

  position: relative;
  display: flex;
  justify-content: center;
  flex-direction: column;
  backface-visibility: hidden;
  outline: 0;
  min-width: 18px;
  gap: 8px;

  min-height: ${({ minHeight }) => minHeight || '24px'};

  ${({ isRightToLeft }) => isRightToLeft && 'justify-content: flex-end;'}
`;
const DefaultCheckboxInput = css `
  & + label {
    &::before {
      ${({ theme }) => theme === LIGHT_TYPE
    ? `
            background-color: ${defaultTransparent};
            border-color: ${colorBlackTransparent200};
          `
    : `
            background-color: ${defaultTransparent};
            border-color: ${colorCoreWhite1000};
          `}
    }

    &::after {
      background-image: none;

      ${({ theme }) => theme !== LIGHT_TYPE && 'filter: invert(1);'}
    }
  }
`;
const ActiveCheckboxInputBg = css `
  &::before {
    ${({ theme }) => theme === LIGHT_TYPE
    ? `
          background-color: ${colorCoreBlack900Base};
          border-color: ${colorCoreBlack900Base};`
    : `
          border-color: ${colorWhiteTransparent800};
          background-color: ${colorWhiteTransparent800};
        `}
  }
`;
const ActiveCheckboxInput = css `
  & + label {
    ${ActiveCheckboxInputBg}

    &::after {
      background-image: url(${CheckboxCheck});
    }
  }
`;
const ActiveMultipleChecked = css `
  & + label {
    ${ActiveCheckboxInputBg}

    &::after {
      background-image: url(${CheckboxUncheck});
    }
  }
`;
const DisabledCheckboxInput = css `
  &:disabled {
    & + label {
      color: ${colorCoreBlack300};

      &::before {
        border-color: ${colorBlackTransparent100};
      }

      &:hover {
        cursor: not-allowed;
      }
    }
  }

  &:checked:disabled + label {
    color: ${colorCoreBlack300};

    &::before {
      background-color: ${({ theme }) => theme === LIGHT_TYPE ? `${extraGrayColor}` : `${colorWhiteTransparent600}`};

      border-color: ${({ theme }) => theme === LIGHT_TYPE ? `${extraGrayColor}` : `${colorWhiteTransparent600}`};
    }
  }
`;
const CheckboxInputDefaultStyled = styled.input `
  position: absolute;
  top: 2px;
  width: 18px;
  height: 18px;
  margin: 0;
  outline: 0;
  cursor: pointer;

  z-index: ${Z_INDEX.level3};

  text-indent: -9999px;
  opacity: 0 !important; /* Extremely need use flag important to cover default input styles */

  ${getTextStyles('Ubuntu', 400, 16, 24)}
`;
export const CheckboxInputStyled = styled(CheckboxInputDefaultStyled) `
  ${DefaultCheckboxInput}

  // Disabled views
  ${DisabledCheckboxInput}

  ${({ isRightToLeft }) => (isRightToLeft ? 'right: 3px' : 'left: 3px')};

  ${({ hasMultipleChecked, checked }) => checked &&
    !hasMultipleChecked &&
    css `
      ${ActiveCheckboxInput}
    `}

  ${({ hasMultipleChecked, checked }) => checked &&
    hasMultipleChecked &&
    css `
      ${ActiveMultipleChecked}
    `}
  
  /* A11Y tab + focus behavior  */
  &:focus + label:before {
    outline: 2px solid ${defaultOutlineColor};
  }

  /* The same style - For support Backport case */
  &:active:not([disabled]) {
    ${({ hasMultipleChecked }) => hasMultipleChecked
    ? css `
            ${ActiveMultipleChecked}
          `
    : css `
            ${ActiveCheckboxInput}
          `}
  }
`;
export const LabelStyled = styled.label `
  ${getTextStyles('Ubuntu', 400, 16, 24)}

  display: flex;
  flex-direction: row;
  padding: 0;
  cursor: pointer;
  position: relative;
  outline: 0;
  min-width: 24px;
  gap: 4px;
  align-items: flex-start;
  user-select: none;

  /* Styles only for text of Label */
  color: ${({ theme }) => theme === LIGHT_TYPE ? `${colorCoreBlack900Base}` : `${colorWhiteTransparent800}`};

  ${({ hasLabelIndent, isRightToLeft }) => hasLabelIndent &&
    isRightToLeft &&
    css `
      padding-right: ${CUSTOM_LABEL_INDENT};
    `}

  ${({ hasLabelIndent, isRightToLeft }) => hasLabelIndent &&
    !isRightToLeft &&
    css `
      padding-left: ${CUSTOM_LABEL_INDENT};
    `}
  
  &::before,
  &::after {
    content: '';
    box-sizing: border-box;
    position: absolute;
    width: 18px;
    height: 18px;
    top: 3px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    ${({ isRightToLeft }) => (isRightToLeft ? 'right: 3px;' : 'left: 3px;')}
  }

  &::before {
    border-width: 2px;
    border-style: solid;
    border-radius: 3px; /* This is custom radius - not from constants */
  }

  &::after {
    padding: 5px 4px;
    background-repeat: no-repeat;
    background-position: center center;
    background-size: auto;
  }

  & > span {
    min-height: 24px;
  }
`;
export const PlaceholderStyled = styled(RegularTextLevel5) `
  ${({ isRightToLeft }) => isRightToLeft
    ? `padding-right: ${CUSTOM_LABEL_INDENT};`
    : `padding-left: ${CUSTOM_LABEL_INDENT};`};
`;
export const CheckboxStyled = styled.div `
  position: relative;
  display: flex;
  align-items: center;
`;
